import {
  PokemonType,
  SkillData,
  SkillType,
  SkillRangeType,
  SkillEvent_Vfx,
  SkillEvent_ProjectileCast,
  SkillEvent_AnchoredInvoke,
  StatusEffect,
  TargetedStatusEffect,
  SkillFinisher,
  SkillTeamCondition,
  TeamConditionEffect,
} from "types/battle-engine/battle";
import { parseSkillIdToName } from "../utils/parse-id-to-name";
import { SQUARE_1_PATTERN } from "../skill-patterns/square-1";
import { msToTicks } from "../utils/ms-to-ticks";

// Simple Projectile Skills - 1 per PokeType
// Basic ranged attack
export const [
  RAZOR_LEAF,
  EMBER,
  BUBBLE,
  THUNDER_SHOCK,
  ICE_SHARD,
  ACID,
  MUD_SHOT,
  GUST,
  PSY_BALL,
  BUG_BUZZ,
  ROCK_THROW,
  SHADOW_DAGGER,
  SCALE_SHOT,
  DARK_PULSE,
  SCRAP_THROW,
  SWEET_KISS,
]: SkillData[] = [
  {
    id: "razor-leaf",
    type: PokemonType.GRASS,
    impact_sprite: "tiny-green-1",
    scale: 1.5,
    description:
      "Sharp leaves fly toward the enemy, cutting through the air with precision.",
  },
  {
    id: "ember",
    type: PokemonType.FIRE,
    impact_sprite: "tiny-red-1",
    description: "Small flames are shot at the target.",
  },
  {
    id: "bubble",
    type: PokemonType.WATER,
    impact_sprite: "tiny-blue-1",
    scale: 0.65,
    description: "A stream of bubbles is fired at the enemy.",
  },
  {
    id: "thunder-shock",
    type: PokemonType.ELECTRIC,
    impact_sprite: "tiny-yellow-1",
    description: "An electric jolt strikes the target.",
  },
  {
    id: "ice-shard",
    type: PokemonType.ICE,
    impact_sprite: "tiny-blue-1",
    description: "A shard of ice is hurled at the enemy with freezing force.",
  },
  {
    id: "acid",
    type: PokemonType.POISON,
    impact_sprite: "tiny-green-1",
    description:
      "Corrosive acid is sprayed at the target, dealing poison damage.",
  },
  {
    id: "mud-shot",
    type: PokemonType.GROUND,
    impact_sprite: "fx3_splatter_large_orange",
    description: "A blob of mud is thrown.",
  },
  {
    id: "gust",
    type: PokemonType.FLYING,
    impact_sprite: "tiny-white-1",
    scale: 1.75,
    description: "A powerful gust of wind blows toward the enemy.",
  },
  {
    id: "psy-ball",
    type: PokemonType.PSYCHIC,
    impact_sprite: "tiny-pink-1",
    description: "A ball of psychic energy is launched at the target.",
  },
  {
    id: "bug-buzz",
    type: PokemonType.BUG,
    impact_sprite: "tiny-green-1",
    description: "A buzzing sound wave attacks the enemy with bug energy.",
  },
  {
    id: "rock-throw",
    type: PokemonType.ROCK,
    impact_sprite: "tiny-white-1",
    description: "A stone is thrown with great force at the enemy.",
  },
  {
    id: "shadow-dagger",
    type: PokemonType.GHOST,
    scale: 1.66,
    impact_sprite: "tiny-white-1",
    description: "A shadowy blade materializes and strikes the target.",
  },
  {
    id: "scale-shot",
    type: PokemonType.DRAGON,
    impact_sprite: "tiny-red-1",
    description: "Scales are fired like projectiles at the enemy.",
  },
  {
    id: "dark-pulse",
    type: PokemonType.DARK,
    impact_sprite: "tiny-white-1",
    description: "A pulse of dark energy engulfs the target.",
  },
  {
    id: "scrap-throw",
    type: PokemonType.STEEL,
    impact_sprite: "tiny-white-1",
    scale: 1.4,
    description: "Scrap metal is thrown at the enemy with metallic force.",
  },
  {
    id: "sweet-kiss",
    type: PokemonType.FAIRY,
    impact_sprite: "tiny-pink-1",
    scale: 3,
    impact_scale: 2,
    impact_speed: 0.35,
    description: "A magical kiss is blown toward the target.",
  },
].map((skill) => ({
  id: skill.id,
  event_checks:
    "event_checks" in skill
      ? (skill.event_checks as SkillData["event_checks"])
      : undefined,
  momentum:
    "momentum" in skill ? (skill.momentum as SkillData["momentum"]) : undefined,
  team_conditions:
    "team_conditions" in skill
      ? (skill.team_conditions as SkillData["team_conditions"])
      : undefined,
  starts_active:
    "starts_active" in skill ? (skill.starts_active as boolean) : undefined,
  name: parseSkillIdToName(skill.id),
  description: skill.description,
  power: 10,
  pokemon_type: skill.type,
  types: [SkillType.DAMAGE],
  cooldown_ticks: 190,
  mana_cost: 5,
  range: {
    type: SkillRangeType.POINT_TARGET,
    value: 5,
  },
  on_hit: [
    {
      __typename: "Vfx",
      duration_ticks: 50,
      target: {
        type: "enemy",
        id: 0,
      },
      status_effects: [],
      starting_tick: 0,
      sprite_path: `/assets/spells/impact/${skill.impact_sprite}.png`,
      sprite_scale: 1.5,
      speed: 0.4,
      blocks_event_pipeline: false,
    } as SkillEvent_Vfx,
  ],
  on_cast: [
    {
      __typename: "ProjectileCast",
      sprite_path: `/assets/spells/projectile/${skill.id}.png`,
      duration_ticks: 51,
      damage: 10,
      speed: 5,
      sprite_scale: skill.scale ?? 1,
      target: {
        type: "enemy",
        id: 0,
      },
      status_effects: [],
      starting_tick: 0,
      blocks_event_pipeline: true,
    } as SkillEvent_ProjectileCast,
  ],
}));

// Projectile Plus Skills - 1 per PokeType
export const [
  SUPERSONIC,
  FIRE_BALL,
  BUBBLE_BEAM,
  BULLET_SEED,
  THUNDERBOLT,
  ICICLE_DAGGER,
  SLUDGE_BOMB,
  MUD_BOMB,
  CHATTER,
  PSY_PULSE,
  STRING_SHOT,
  ROCK_SLIDE,
  SHADOW_BALL,
  DRAGON_PULSE,
  FLATTER,
  MAGNET_BOMB,
  DAZZLING_GLEAM,
  ENERGY_BALL,
  CONFUSE_RAY,
] = [
  {
    id: "supersonic",
    type: PokemonType.NORMAL,
    impact_sprite: "tiny-white-1",
    description:
      "Emits confusing sound waves. Apply CONFUSION to the target for 1 second.",
    status_effects: [
      { status: StatusEffect.CONFUSION, duration_ticks: 62, intensity: 1 },
    ],
  },
  {
    id: "fire-ball",
    power: 2,
    cooldown_ticks: 240,
    type: PokemonType.FIRE,
    status_effects: [
      { status: StatusEffect.BURN, duration_ticks: 999999, intensity: 2 },
    ],
    impact_sprite: "tiny-red-1",
    impact_scale: 1.5,
    impact_speed: 0.35,
    scale: 0.8,
    aoe_hit: {
      tile_sprite: "/assets/spells/tile-fx/magic-puff-red.png",
      pattern: SQUARE_1_PATTERN().North,
    },
    description:
      "A fiery projectile that explodes on impact. Applies 2x 🔥BURN to targets.",
  },
  {
    id: "bubble-beam",
    power: 2,
    type: PokemonType.WATER,
    impact_sprite: "tiny-blue-1",
    multihit: 4,
    scale: 0.75,
    description: "Fires 4 bubbles that strike the target repeatedly.",
  },
  {
    id: "bullet-seed",
    power: 2,
    type: PokemonType.GRASS,
    impact_sprite: "tiny-green-1",
    scale: 2,
    multihit: 4,
    description: "Rapidly fires 4 seeds like bullets at the enemy.",
  },
  {
    id: "thunderbolt",
    cooldown_ticks: 200,
    type: PokemonType.ELECTRIC,
    impact_sprite: "tiny-yellow-1",
    status_effects: [
      { status: StatusEffect.STUN, duration_ticks: 30, intensity: 1 },
    ],
    description:
      "A powerful electric bolt. Applies 💫STUN to the target for 0.5 seconcds.",
  },
  {
    id: "icicle-dagger",
    type: PokemonType.ICE,
    impact_sprite: "tiny-blue-1",
    scale: 2,
    finisher: {
      threshold_hp_percent: 30,
      damage_multiplier: 1,
      status_effects: [
        {
          effect: {
            status: StatusEffect.FREEZE,
            duration_ticks: 62,
            intensity: 1,
          },
          target: "target",
        } as TargetedStatusEffect,
      ],
    } as SkillFinisher,
    description:
      "Sharp icicles are hurled at the enemy with deadly precision. If the target is below 30% HP, apply ❄️FREEZE for 1 second.",
  },
  {
    id: "sludge-bomb",
    type: PokemonType.POISON,
    impact_sprite: "impfx2_dust_impact_A_large_violet",
    impact_scale: 1.5,
    scale: 1.5,
    impact_speed: 0.35,
    status_effects: [
      { status: StatusEffect.POISON, duration_ticks: 99999, intensity: 1 },
    ],
    description:
      "Toxic sludge is thrown at the enemy. Apply 🟣POISON to the target.",
  },
  {
    id: "mud-bomb",
    type: PokemonType.GROUND,
    impact_sprite: "tiny-orange-1", // TODO => proper impact for mud
    description:
      "A blob of mud is launched at the enemy. Apply SLOW to the target for 2 seconds.",
    status_effects: [
      { status: StatusEffect.SPEED_DOWN, duration_ticks: 124, intensity: 1 },
    ],
  },
  {
    id: "chatter",
    type: PokemonType.FLYING,
    impact_sprite: "tiny-white-1",
    description: "Annoying chatter that damages the target.",
  },
  {
    id: "psy-pulse",
    type: PokemonType.PSYCHIC,
    cooldown_ticks: 260,
    impact_sprite: "tiny-pink-1",
    description:
      "Psychic pulses are shot at the target. This spell has 50% less cooldown if there is a Dragon-type ally on the team.",
    team_conditions: [
      {
        condition: {
          type: "min_ally_type_count",
          pokemon_type: PokemonType.DRAGON,
          value: 1,
        },
        effect: {
          stat: "cooldown_recharge",
          value: 0.5,
        } as TeamConditionEffect,
      } as SkillTeamCondition,
    ],
  },
  {
    id: "string-shot",
    cooldown_ticks: 200,
    scale: 1.5,
    type: PokemonType.BUG,
    impact_sprite: "tiny-green-1",
    impact_scale: 1.5,
    status_effects: [
      { status: StatusEffect.SPEED_DOWN, duration_ticks: 124, intensity: 1 },
    ],
    description:
      "Sticky string shot that slows down the enemy. Apply SLOW to the target for 2 seconds.",
  },
  {
    id: "rock-slide",
    type: PokemonType.ROCK,
    impact_sprite: "tiny-white-1",
    multihit: 3,
    scale: 1.15,
    description: "Rocks tumble down, striking the target multiple times.",
  },
  {
    id: "shadow-ball",
    type: PokemonType.GHOST,
    scale: 1.5,
    impact_sprite: "fx3_smoke_bomb_large_black",
    impact_scale: 1.75,
    impact_speed: 0.35,
    status_effects: [
      { status: StatusEffect.ATTACK_DOWN, duration_ticks: 124, intensity: 1 },
    ],
    description:
      "A large shadowy sphere that reduces the target ability power. Apply ATTACK DOWN to the target for 2 seconds.",
  },
  {
    id: "dragon-pulse",
    type: PokemonType.DRAGON,
    scale: 1.5,
    impact_sprite: "tiny-blue-1",
    aoe_hit: {
      tile_sprite: "/assets/spells/tile-fx/magic-puff-blue.png",
      pattern: SQUARE_1_PATTERN().North,
    },
    cooldown_ticks: msToTicks(8000),
    status_effects: [
      { status: StatusEffect.BURN, duration_ticks: 999999, intensity: 1 },
    ],
    description:
      "A powerful dragon energy wave that creates a small explosion. Apply 🔥BURN to the targets.",
  },
  {
    id: "flatter",
    type: PokemonType.DARK,
    impact_sprite: "fx3_smoke_bomb_large_black",
    description:
      "Flattering words that damages the target and raises user's ability power for 2.5 seconds.",
    self_status_effects: [
      { status: StatusEffect.ATTACK_UP, duration_ticks: 186, intensity: 1 },
    ],
  },
  {
    id: "magnet-bomb",
    type: PokemonType.STEEL,
    power: 7,
    cooldown_ticks: 230,
    scale: 1.25,
    impact_sprite: "impfx2_scifi_impact_B_large_white",
    impact_scale: 1.25,
    impact_speed: 0.35,
    status_effects: [
      { status: StatusEffect.STUN, duration_ticks: 100, intensity: 1 },
    ],
    description: "Magnetic projectile shot that damages and stuns the target.",
  },
  {
    id: "dazzling-gleam",
    type: PokemonType.FAIRY,
    cooldown_ticks: 225,
    impact_sprite: "tiny-pink-1",
    impact_scale: 1,
    impact_speed: 0.5,
    multihit: 3,
    description: "Dazzling light strikes the target multiple times.",
  },
  {
    id: "energy-ball",
    type: PokemonType.GRASS,
    impact_sprite: "tiny-green-1",
    scale: 1.5,
    cooldown_ticks: msToTicks(6000),
    aoe_hit: {
      tile_sprite: "/assets/spells/tile-fx/magic-puff-green.png",
      pattern: SQUARE_1_PATTERN().North,
    },
    description:
      "A ball of nature energy that damages and lowers target defense for 2 seconds.",
    status_effects: [
      {
        status: StatusEffect.DEFENSE_DOWN,
        duration_ticks: msToTicks(2000),
        intensity: 1,
      },
    ],
  },
  {
    id: "confuse-ray",
    type: PokemonType.GHOST,
    cooldown_ticks: 250,
    impact_sprite: "tiny-pink-1",
    scale: 0.5,
    status_effects: [
      { status: StatusEffect.CONFUSION, duration_ticks: 62, intensity: 1 },
    ],
    multihit: 3,
    description:
      "Strange rays that confuse the target with multiple hits. Apply CONFUSION to the target for 1 second.",
  },
].map((skill) => ({
  id: skill.id,
  event_checks:
    "event_checks" in skill
      ? (skill.event_checks as SkillData["event_checks"])
      : undefined,
  momentum:
    "momentum" in skill ? (skill.momentum as SkillData["momentum"]) : undefined,
  team_conditions:
    "team_conditions" in skill
      ? (skill.team_conditions as SkillData["team_conditions"])
      : undefined,
  starts_active:
    "starts_active" in skill ? (skill.starts_active as boolean) : undefined,
  name: parseSkillIdToName(skill.id),
  description: skill.description,
  power: skill.power ?? 10,
  pokemon_type: skill.type,
  types: [SkillType.DAMAGE],
  cooldown_ticks: skill.cooldown_ticks ?? 150,
  mana_cost: 10,
  range: {
    type: SkillRangeType.POINT_TARGET,
    value: 5,
  },
  on_hit: [
    {
      __typename: "Vfx",
      duration_ticks: 50,
      target: {
        type: "enemy",
        id: 0,
      },
      status_effects: skill.status_effects ?? [],
      starting_tick: 0,
      sprite_path: `/assets/spells/impact/${skill.impact_sprite}.png`,
      sprite_scale: skill.impact_scale ?? 1,
      speed: skill.impact_speed ?? 0.7,
      blocks_event_pipeline: false,
    } as SkillEvent_Vfx,
    ...(skill.aoe_hit
      ? [
        ...skill.aoe_hit.pattern.map(
          ([x, y]) =>
            ({
              __typename: "AnchoredInvoke",
              duration_ticks: 15,
              target: {
                type: "enemy",
                id: 0,
              },
              status_effects: skill.status_effects ?? [],
              starting_tick: 0,
              relative_x: x,
              relative_y: y,
              sprite_path: skill.aoe_hit.tile_sprite,
              blocks_event_pipeline: false,
              damage: skill.power ?? 5,
              sprite_scale: skill.scale ?? 1,
            }) as SkillEvent_AnchoredInvoke,
        ),
      ]
      : []),
  ],
  on_cast: skill.multihit
    ? Array.from({ length: skill.multihit }).map(
      (_, index) =>
        ({
          __typename: "ProjectileCast",
          sprite_path: `/assets/spells/projectile/${skill.id}.png`,
          duration_ticks: 10,
          damage: skill.power ?? 10 / skill.multihit,
          speed: 41,
          sprite_scale: skill.scale ?? 1,
          target: {
            type: "enemy",
            id: 0,
          },
          status_effects: [],
          self_status_effects: skill.self_status_effects ?? [],
          starting_tick: 0,
          blocks_event_pipeline: true,
        }) as SkillEvent_ProjectileCast,
    )
    : [
      {
        __typename: "ProjectileCast",
        sprite_path: `/assets/spells/projectile/${skill.id}.png`,
        duration_ticks: 51,
        damage: 10,
        speed: 41,
        sprite_scale: skill.scale ?? 1,
        target: {
          type: "enemy",
          id: 0,
        },
        status_effects: [],
        self_status_effects: skill.self_status_effects ?? [],
        starting_tick: 0,
        blocks_event_pipeline: true,
      } as SkillEvent_ProjectileCast,
    ],
}));
